/**
 * Wrangle-style empty-state illustration for the Emails module.
 *
 * Two small icons connected by a dashed line, sitting on a subtle
 * grid background. Re-used by the Templates and Scheduled tabs so
 * both empty states feel consistent.
 */

import type { LucideIcon } from 'lucide-react';
import { MessageSquare, Mail } from 'lucide-react';

interface Props {
  /** Optional left-side icon. Defaults to chat bubble. */
  leftIcon?: LucideIcon;
  /** Optional right-side icon (the highlighted one). Defaults to envelope. */
  rightIcon?: LucideIcon;
  title: string;
  description: string;
  /** Reduces vertical padding — use when other content follows below. */
  compact?: boolean;
}

export function EmailEmptyArt({
  leftIcon: LeftIcon = MessageSquare,
  rightIcon: RightIcon = Mail,
  title,
  description,
  compact = false,
}: Props) {
  return (
    <div
      className={`flex flex-col items-center justify-center px-6 text-center ${compact ? 'py-6' : 'min-h-[400px] py-10'}`}
    >
      {/* Illustration block */}
      <div
        className="relative mb-6 flex h-[120px] w-[260px] items-center justify-center"
        aria-hidden
      >
        {/* Faint grid background */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          style={{ color: 'var(--vscode-border-primary)', opacity: 0.35 }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="ggj-empty-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ggj-empty-grid)" />
        </svg>

        {/* Left icon tile — muted */}
        <div
          className="relative z-10 flex h-11 w-11 items-center justify-center rounded-[10px] border"
          style={{
            background: 'var(--vscode-bg-elevated)',
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-tertiary)',
          }}
        >
          <LeftIcon className="h-5 w-5" />
        </div>

        {/* Dashed connector */}
        <svg className="relative z-0 mx-2 h-[2px] w-16" aria-hidden>
          <line
            x1="0"
            y1="1"
            x2="100%"
            y2="1"
            stroke="#10B981"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            opacity="0.55"
          />
        </svg>

        {/* Right icon tile — highlighted emerald */}
        <div
          className="relative z-10 flex h-11 w-11 items-center justify-center rounded-[10px] border"
          style={{
            background:
              'color-mix(in srgb, #10B981 10%, var(--vscode-bg-elevated))',
            borderColor:
              'color-mix(in srgb, #10B981 45%, var(--vscode-border-primary))',
            color: '#10B981',
            boxShadow:
              '0 0 0 4px color-mix(in srgb, #10B981 10%, transparent)',
          }}
        >
          <RightIcon className="h-5 w-5" />
        </div>
      </div>

      <h3
        className="text-[14px] font-medium"
        style={{ color: 'var(--vscode-text-primary)' }}
      >
        {title}
      </h3>
      <p
        className="mt-1.5 max-w-[320px] text-[12px] leading-relaxed"
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        {description}
      </p>
    </div>
  );
}
